import * as Sentry from "@sentry/vue";

type MetricAttributes = Record<string, boolean | number | string>;

type PerformanceMemory = {
  usedJSHeapSize: number;
};

type PerformanceWithMemory = Performance & {
  measureUserAgentSpecificMemory?: () => Promise<{ bytes: number }>;
  memory?: PerformanceMemory;
};

const MEMORY_SAMPLE_INTERVAL_MEAN_MS = 5 * 60 * 1000;

let memoryMonitoringStarted = false;

export const recordDuration = (name: string, startedAt: number, attributes?: MetricAttributes) => {
  if (!Sentry.isInitialized()) {
    return;
  }
  Sentry.metrics.distribution(`dart.${name}`, performance.now() - startedAt, {
    unit: "millisecond",
    attributes,
  });
};

export const captureMemorySample = async () => {
  if (!Sentry.isInitialized()) {
    return;
  }

  const performanceWithMemory = performance as PerformanceWithMemory;
  if (window.crossOriginIsolated && performanceWithMemory.measureUserAgentSpecificMemory) {
    try {
      const measurement = await performanceWithMemory.measureUserAgentSpecificMemory();
      Sentry.metrics.gauge("dart.browser.memory_usage", measurement.bytes, {
        unit: "byte",
        attributes: { source: "user_agent_specific" },
      });
      return;
    } catch {
      // Fall back to the Chromium heap API when the more precise API is unavailable at runtime.
    }
  }

  if (performanceWithMemory.memory) {
    Sentry.metrics.gauge("dart.browser.memory_usage", performanceWithMemory.memory.usedJSHeapSize, {
      unit: "byte",
      attributes: { source: "chromium_heap" },
    });
  }
};

export const startMemoryMonitoring = () => {
  const performanceWithMemory = performance as PerformanceWithMemory;
  const hasMemoryApi =
    (window.crossOriginIsolated && !!performanceWithMemory.measureUserAgentSpecificMemory) ||
    !!performanceWithMemory.memory;
  if (memoryMonitoringStarted || !Sentry.isInitialized() || !hasMemoryApi) {
    return;
  }
  memoryMonitoringStarted = true;

  const scheduleMemorySample = () => {
    const delayMs = -Math.log(Math.random()) * MEMORY_SAMPLE_INTERVAL_MEAN_MS;
    // oxlint-disable-next-line dart/no-set-timeout
    window.setTimeout(async () => {
      if (document.visibilityState === "visible") {
        await captureMemorySample();
      }
      scheduleMemorySample();
    }, delayMs);
  };

  captureMemorySample();
  scheduleMemorySample();
};
